import React, { FC, useState, useEffect } from "react";
import ReactFlow, {
  Node,
  Edge,
  ReactFlowProvider,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import useAnalysis from "../pages/samples/utils/useAnalysis";
import { AnalysisWhitMeasurement } from "@app/pages/samples/config/phases/AnalysisPhases";

interface DiagramProps {
  data: any;
}

const Diagram: FC<DiagramProps> = ({ data }) => {
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const { fitView } = useReactFlow();
  const { preparationsList } = useAnalysis();

  const gralNodeWidth = 170;
  const prepNodeWidth = 110;
  const conNodeWidth = 90;
  const calNodeWidth = 90;
  const nodeHeight = 40;

  const minSpacing = 150;
  const conSpacing = 120;
  const calSpacing = 100;

  const calculateContajeWidth = (contaje: any) => {
    const calculations = contaje.calculations || [];
    const calcWidth = calculations.length * (calNodeWidth + calSpacing);
    return Math.max(conNodeWidth, calcWidth);
  };

  const calculatePreparationWidth = (countings: any[]) => {
    if (countings.length === 0) {
      return prepNodeWidth;
    }
    const totalWidth = countings.reduce((acc, count) => {
      return acc + calculateContajeWidth(count) + conSpacing;
    }, -conSpacing);
    return totalWidth;
  };

  const calculateAnalysisWidth = (preparations: any[]) => {
    const totalWidth = preparations.reduce((acc, prep: any) => {
      return acc + calculatePreparationWidth(prep.countings || []) + minSpacing;
    }, -minSpacing);
    return totalWidth;
  };

  const parseDataToElements = (structuredData: any) => {
    const parsedNodes: Node[] = [];
    const parsedEdges: Edge[] = [];
    let nodeId = 1;
    let edgeId = 1;

    const addNode = (
      id: string,
      label: string,
      width: number,
      height: number,
      position: { x: number; y: number }
    ) => {
      parsedNodes.push({
        id,
        data: { label },
        position,
        style: { width, height },
      });
    };

    const addEdge = (source: string, target: string) => {
      parsedEdges.push({
        id: `e${edgeId++}`,
        source,
        target,
        type: "smoothstep",
      });
    };

    const xInitial = 600;
    const yInitial = 50;
    addNode("1", structuredData.sampleName, gralNodeWidth, nodeHeight, {
      x: xInitial,
      y: yInitial,
    });

    let currentXOffset = xInitial;
    const analysisYOffset = yInitial + 150;

    structuredData.analyses.forEach((analysis: any) => {
      const analysisId = `${++nodeId}`;
      const isMeasurementType = AnalysisWhitMeasurement.includes(
        analysis.analysisTypeCode
      );

      const analysisWidth = calculateAnalysisWidth(analysis.preparations);
      const xOffset = currentXOffset - analysisWidth / 2;

      addNode(analysisId, analysis.analysisType, gralNodeWidth, nodeHeight, {
        x: xOffset,
        y: analysisYOffset,
      });
      addEdge("1", analysisId);

      const totalPrepWidth = calculateAnalysisWidth(analysis.preparations);
      const prepXInitial = xOffset - totalPrepWidth / 2;
      let currentX = prepXInitial;

      analysis.preparations.forEach((prep: any, prepIndex: number) => {
        const prepId = `${++nodeId}`;
        const prepWidth = calculatePreparationWidth(prep.countings || []);
        addNode(prepId, `Prep. ${prepIndex + 1}`, prepNodeWidth, nodeHeight, {
          x: currentX + prepWidth / 2,
          y: analysisYOffset + 150,
        });
        addEdge(analysisId, prepId);

        if (isMeasurementType) {
          const measurements = prep.aa_measures || [];
          if (measurements.length === 0) {
            const placeholderId = `${++nodeId}`;
            addEdge(prepId, placeholderId);
          } else {
            const measurementY = analysisYOffset + 300;
            let currentMeasurementX = currentX;

            measurements.forEach((measurement: any, countIndex: number) => {
              const measurementId = `${++nodeId}`;
              const countWidth = calculateContajeWidth(measurement);
              addNode(
                measurementId,
                `Medición. ${countIndex + 1}`,
                conNodeWidth,
                nodeHeight,
                {
                  x: currentMeasurementX + countWidth / 2,
                  y: measurementY,
                }
              );
              addEdge(prepId, measurementId);

              const countings = measurement.countings || [];
              const countingsY = measurementY + 100;
              let currentCountingX = currentMeasurementX;

              if (countings.length === 0) {
                const placeholderCalcId = `${++nodeId}`;
                addEdge(measurementId, placeholderCalcId);
              } else {
                countings.forEach((couting: any, calcIndex: number) => {
                  const countId = `${++nodeId}`;
                  addNode(
                    countId,
                    `Con. ${calcIndex + 1}`,
                    calNodeWidth,
                    nodeHeight,
                    {
                      x:
                        currentCountingX +
                        calcIndex * (calNodeWidth + calSpacing),
                      y: countingsY,
                    }
                  );
                  addEdge(measurementId, countId);

                  const calculations = couting.calculations || [];
                  const calculationY = countingsY + 100;
                  let currentCalcX = currentCountingX;

                  if (calculations.length === 0) {
                    const placeholderCalcId = `${++nodeId}`;
                    addEdge(countId, placeholderCalcId);
                  } else {
                    calculations.forEach((calc: any, calcIndex: number) => {
                      const calcId = `${++nodeId}`;
                      addNode(
                        calcId,
                        `Cál. ${calcIndex + 1}`,
                        calNodeWidth,
                        nodeHeight,
                        {
                          x:
                            currentCalcX +
                            calcIndex * (calNodeWidth + calSpacing),
                          y: calculationY,
                        }
                      );
                      addEdge(countId, calcId);
                    });
                  }
                });
              }
              currentCountingX += countWidth + conSpacing;
            });
          }
        } else {
          const countings = prep.countings || [];
          if (countings.length === 0) {
            const placeholderId = `${++nodeId}`;
            addEdge(prepId, placeholderId);
          } else {
            const countingY = analysisYOffset + 300;
            let currentCountingX = currentX;

            countings.forEach((count: any, countIndex: number) => {
              const countId = `${++nodeId}`;
              const countWidth = calculateContajeWidth(count);
              addNode(
                countId,
                `Con. ${countIndex + 1}`,
                conNodeWidth,
                nodeHeight,
                {
                  x: currentCountingX + countWidth / 2,
                  y: countingY,
                }
              );
              addEdge(prepId, countId);

              const calculations = count.calculations || [];
              const calculationY = countingY + 100;
              let currentCalcX = currentCountingX;

              if (calculations.length === 0) {
                const placeholderCalcId = `${++nodeId}`;
                addEdge(countId, placeholderCalcId);
              } else {
                calculations.forEach((calc: any, calcIndex: number) => {
                  const calcId = `${++nodeId}`;
                  addNode(
                    calcId,
                    `Cál. ${calcIndex + 1}`,
                    calNodeWidth,
                    nodeHeight,
                    {
                      x: currentCalcX + calcIndex * (calNodeWidth + calSpacing),
                      y: calculationY,
                    }
                  );
                  addEdge(countId, calcId);
                });
              }
              currentCountingX += countWidth + conSpacing;
            });
          }
        }
        currentX += prepWidth + minSpacing;
      });

      currentXOffset += analysisWidth + minSpacing;
    });

    setNodes(parsedNodes);
    setEdges(parsedEdges);
  };

  useEffect(() => {
    const structuredSampleData = {
      sampleName: `Muestra N° ${data.masl_reference}`,
      analyses: data.analysis_type.map((analysis: any) => ({
        analysisType: `Análisis ${analysis.descripcion}`,
        analysisTypeCode: analysis.codigo,
        preparations: preparationsList.filter((prep: any) =>
          prep.analysis_type.some((a: any) => a.id === analysis.id)
        ),
      })),
    };
    parseDataToElements(structuredSampleData);
  }, [data, preparationsList]);

  useEffect(() => {
    fitView();
  }, [nodes, edges]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      style={{ width: "100%", height: "100%" }}
    />
  );
};

const WrappedDiagram: FC<DiagramProps> = (props) => (
  <ReactFlowProvider>
    <Diagram {...props} />
  </ReactFlowProvider>
);

export default WrappedDiagram;
